import React, { useState } from 'react';
import { Document, Page } from 'react-pdf';
import { Box, Typography } from '@mui/material';
import { Button, Loading } from 'framework/ui/mui-builder';
import { getFileExtension } from '../../../artifact/utils';
import useDocumentPreviewService from '../../documentPreviewService';
import { useArtifactDownloadHelper } from 'domain/shared/artifact';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { DEFAULT_ERROR_MESSAGE } from 'framework/form';

type Props = {
  documentPath?: string;
  blobUrl: Nullable<string>;
  loading: boolean;
  hasError: Nullable<boolean>;
  defaultScale?: number;
  messageContext?: string;
};

const DocumentPreviewRenderer = ({ documentPath, blobUrl, loading, hasError, defaultScale, messageContext }: Props) => {
  const [numPages, setNumPages] = useState(0);
  const { addMessage } = useMessageService(messageContext);
  const { isPreviewAvailable, isPreviewImage } = useDocumentPreviewService();
  const { downloadDocumentStorage } = useArtifactDownloadHelper();

  // eslint-disable-next-line no-nested-ternary
  const fileExtension = documentPath ? getFileExtension(documentPath) : blobUrl ? getFileExtension(blobUrl) : null;

  const handleDownload = async () => {
    if (!documentPath) return;
    try {
      await downloadDocumentStorage(documentPath);
    } catch (e) {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    }
  };

  const renderPreviewContent = () => {
    if (loading) return <Loading />;

    if (fileExtension && isPreviewAvailable(fileExtension) && !hasError) {
      return (
        <Document file={blobUrl} onLoadSuccess={({ numPages: pages }) => setNumPages(pages)}>
          {Array.from({ length: numPages }, (_, index) => (
            <Box
              key={`page_${index}`}
              mb={2}
              borderBottom={index + 1 < numPages ? '1px solid var(--mui-palette-border-medium)' : ''}
              pb={2}
            >
              <Page renderAnnotationLayer={false} pageNumber={index + 1} width={700} scale={defaultScale} />
            </Box>
          ))}
        </Document>
      );
    }

    if (fileExtension && isPreviewImage(fileExtension) && !hasError) {
      return (
        <Box
          component="img"
          data-testid="lightbox-img"
          alt=""
          src={blobUrl!}
          sx={{ maxHeight: '100%', maxWidth: '100%', opacity: 1, transition: 'opacity 0.3s ease' }}
        />
      );
    }

    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        <Typography color="primary" variant="h3">
          This file cannot be viewed!
        </Typography>
        {documentPath && (
          <Button onClick={handleDownload} link>
            Download
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box display="flex" flexDirection="column" height="stretch" mb="10px">
      <Typography textAlign="end" color="primary">
        Preview
      </Typography>
      <Box
        display="flex"
        alignItems="center"
        flexDirection="column"
        height="stretch"
        sx={{
          minHeight: '400px',
          position: 'relative',
          overflow: 'auto',
          border: '1px solid var(--mui-palette-TableCell-border)',
          borderRadius: 1,
          padding: '5px',
        }}
      >
        <Box sx={{ maxHeight: '400px' }}>{renderPreviewContent()}</Box>
      </Box>
    </Box>
  );
};

export default DocumentPreviewRenderer;
